import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ConfigService } from '../../shared-services/config.service';
import { ConfirmationDialogService } from '../../shared-services/confirmation-dialog.service';

type FormFieldOption = string | { label: string; value: any };
type FormFieldType = 'text' | 'number' | 'select' | 'select-checkbox' | 'email' | 'tel' | 'textarea';

interface FormFieldConfig {
  key: string;
  label: string;
  type: FormFieldType;
  placeholder?: string;
  required?: boolean;
  defaultValue?: any;
  colSpan?: number;
  options?: FormFieldOption[];
  optionsApiUrl?: string;
  optionsMethod?: string;
  optionsPayload?: any;
  optionsResponsePath?: string;
  optionsLabelKey?: string;
  optionsValueKey?: string;
}

interface FormStepConfig {
  number?: number | string;
  label: string;
  active?: boolean;
}

interface FormConfirmationConfig {
  enabled?: boolean;
  title: string;
  message: string;
  showActions?: boolean;
  isError?: boolean;
  proceedOnDismiss?: boolean;
  confirmLabel?: string;
  cancelLabel?: string;
  inputs?: Array<{
    key: string;
    label?: string;
    type?: 'text' | 'number' | 'email' | 'tel' | 'textarea';
    placeholder?: string;
    required?: boolean;
    defaultValue?: any;
  }>;
}

interface ConfigurableFormConfig {
  title?: string;
  description?: string;
  headerColor?: string;
  height?: string;
  alignment?: 'left' | 'center' | 'right';
  steps?: FormStepConfig[];
  fields?: FormFieldConfig[];
  submitLabel?: string;
  method?: string;
  submitApiUrl?: string;
  submitMethod?: string;
  submitSuccessMessage?: string;
  submitErrorMessage?: string;
  submitPayload?: any;
  submitConfirmation?: FormConfirmationConfig;
  confirmationDialog?: FormConfirmationConfig;
  skipDefaultPayload?: boolean;
  skipDefaultPayloadForSubmit?: boolean;
  headers?: Record<string, string>;
  layout?: {
    columns?: number;
    gap?: string;
  };
}

@Component({
  selector: 'app-configurable-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './configurable-form.component.html',
  styleUrl: './configurable-form.component.scss',
})
export class ConfigurableFormComponent implements OnInit, OnChanges {
  @Input() data?: ConfigurableFormConfig & { apiUrl?: string };
  @Output() submitted = new EventEmitter<Record<string, any>>();

  form = new FormGroup({});
  fields: FormFieldConfig[] = [];
  steps: FormStepConfig[] = [];
  title = '';
  description = '';
  headerColor = '';
  height = '';
  alignment: 'left' | 'center' | 'right' = 'left';
  submitLabel = 'Submit';
  isLoading = false;
  isSubmitting = false;
  submitMessage = '';
  submitError = '';
  checkboxSearchTerms: Record<string, string> = {};
  private apiConfigResponse: any;

  constructor(
    private configService: ConfigService,
    private confirmationDialogService: ConfirmationDialogService,
  ) {}

  ngOnInit(): void {
    this.initializeForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && !changes['data']?.isFirstChange()) {
      this.initializeForm();
    }
  }

  get layoutColumns(): number {
    return this.data?.layout?.columns || 2;
  }

  get layoutGap(): string {
    return this.data?.layout?.gap || '24px';
  }

  trackByKey(_: number, field: FormFieldConfig): string {
    return field.key;
  }

  isFieldInvalid(field: FormFieldConfig): boolean {
    const control = this.form.get(field.key);
    return !!control && control.invalid && (control.dirty || control.touched);
  }

  getOptionLabel(option: FormFieldOption): string {
    return typeof option === 'string' ? option : option.label;
  }

  getOptionValue(option: FormFieldOption): any {
    return typeof option === 'string' ? option : option.value;
  }

  getFieldType(field: FormFieldConfig): string {
    return field.type === 'select' || field.type === 'select-checkbox' || field.type === 'textarea'
      ? 'text'
      : field.type;
  }

  isOptionSelected(field: FormFieldConfig, option: FormFieldOption): boolean {
    const value = this.form.get(field.key)?.value;
    return Array.isArray(value) && value.includes(this.getOptionValue(option));
  }

  getSelectedOptions(field: FormFieldConfig): FormFieldOption[] {
    const value = this.form.get(field.key)?.value;
    if (!Array.isArray(value)) {
      return [];
    }
    return (field.options || []).filter((option) => value.includes(this.getOptionValue(option)));
  }

  getFilteredCheckboxOptions(field: FormFieldConfig): FormFieldOption[] {
    const searchTerm = (this.checkboxSearchTerms[field.key] || '').trim().toLowerCase();
    const options = field.options || [];
    if (!searchTerm) {
      return options;
    }

    return options.filter((option) =>
      this.getOptionLabel(option).toLowerCase().includes(searchTerm),
    );
  }

  onCheckboxSearchChange(field: FormFieldConfig, value: string): void {
    this.checkboxSearchTerms[field.key] = value;
  }

  onCheckboxOptionChange(field: FormFieldConfig, option: FormFieldOption, checked: boolean): void {
    const control = this.form.get(field.key);
    if (!control) {
      return;
    }

    const optionValue = this.getOptionValue(option);
    const currentValue = Array.isArray(control.value) ? [...control.value] : [];
    const nextValue = checked
      ? Array.from(new Set([...currentValue, optionValue]))
      : currentValue.filter((value) => value !== optionValue);

    control.setValue(nextValue);
    control.markAsDirty();
    control.markAsTouched();
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const formValue = this.form.getRawValue();
    const confirmationConfig = this.resolveSubmitConfirmation(formValue);

    if (confirmationConfig) {
      this.confirmationDialogService.open(confirmationConfig).subscribe((result) => {
        const confirmed = typeof result === 'object' ? result.confirmed : result;
        const dialogValues = typeof result === 'object' ? result.values || {} : {};
        if (confirmed || confirmationConfig.proceedOnDismiss === true) {
          this.submitForm(formValue, dialogValues);
        }
      });
      return;
    }

    this.submitForm(formValue);
  }

  private submitForm(
    formValue: Record<string, any>,
    dialogValues: Record<string, any> = {},
  ): void {
    const payloadValue = {
      ...formValue,
      ...dialogValues,
    };

    this.submitted.emit(payloadValue);
    this.submitMessage = '';
    this.submitError = '';

    if (!this.data?.submitApiUrl) {
      return;
    }

    this.isSubmitting = true;
    const submitData = {
      ...this.data,
      apiUrl: this.data.submitApiUrl,
      method: this.data.submitMethod || this.data.method || 'post',
      customPayload: {
        ...(this.data.submitPayload || {}),
        ...payloadValue,
      },
      skipDefaultPayload: this.data.skipDefaultPayloadForSubmit ?? this.data.skipDefaultPayload,
    };

    this.configService
      .getConfig(this.data.submitApiUrl, submitData, undefined, submitData.method)
      .subscribe({
        next: () => {
          this.submitMessage = this.data?.submitSuccessMessage || 'Submitted successfully.';
          this.isSubmitting = false;
        },
        error: () => {
          this.submitError = this.data?.submitErrorMessage || 'Unable to submit the form.';
          this.isSubmitting = false;
        },
      });
  }

  private resolveSubmitConfirmation(formValue: Record<string, any>): FormConfirmationConfig | null {
    const config = this.data?.submitConfirmation || this.data?.confirmationDialog;
    if (!config || config.enabled === false) {
      return null;
    }

    return {
      ...config,
      title: this.interpolateTemplate(config.title, formValue),
      message: this.interpolateTemplate(config.message, formValue),
      showActions: config.showActions ?? true,
    };
  }

  private interpolateTemplate(template: string, values: Record<string, any>): string {
    return String(template || '').replace(/\{([^}]+)\}/g, (_, key: string) => {
      const value = values[key.trim()];
      if (Array.isArray(value)) {
        return value.join(', ');
      }
      return value ?? '';
    });
  }

  private initializeForm(): void {
    if (!this.data) {
      return;
    }

    if (this.data.apiUrl) {
      this.isLoading = true;
      const method = this.data?.method || 'get';
      this.configService.getConfig(this.data.apiUrl, this.data, undefined, method).subscribe({
        next: (config: any) => {
          this.apiConfigResponse = config;
          this.applyConfig(this.mergeConfig(config as ConfigurableFormConfig));
          this.isLoading = false;
        },
        error: () => {
          this.apiConfigResponse = undefined;
          this.applyConfig(this.mergeConfig());
          this.isLoading = false;
        },
      });
      return;
    }

    this.applyConfig(this.mergeConfig());
  }

  private mergeConfig(apiConfig?: ConfigurableFormConfig): ConfigurableFormConfig {
    const dataConfig = this.data || {};
    return {
      ...apiConfig,
      ...dataConfig,
      fields: dataConfig.fields || apiConfig?.fields || [],
      steps: dataConfig.steps || apiConfig?.steps || [],
    };
  }

  private applyConfig(config: ConfigurableFormConfig): void {
    this.title = config.title || '';
    this.description = config.description || '';
    this.headerColor = config.headerColor || '';
    this.height = config.height || '';
    this.alignment = config.alignment || 'left';
    this.fields = config.fields || [];
    this.steps = config.steps || [];
    this.submitLabel = config.submitLabel || 'Submit';
    this.checkboxSearchTerms = {};
    this.buildFormGroup();
    this.loadFieldOptions();
  }

  private buildFormGroup(): void {
    const group: { [key: string]: FormControl } = {};

    this.fields.forEach((field) => {
      const validators = field.required ? [Validators.required] : [];
      const defaultValue = field.defaultValue ?? (field.type === 'select-checkbox' ? [] : '');
      group[field.key] = new FormControl(defaultValue, validators);
    });

    this.form = new FormGroup(group);
  }

  private loadFieldOptions(): void {
    this.fields.forEach((field) => {
      if (field.options && field.options.length > 0) {
        return;
      }

      if (!field.optionsApiUrl && this.apiConfigResponse) {
        field.options = this.normalizeOptions(this.apiConfigResponse, field);
        return;
      }

      if (!field.optionsApiUrl) {
        return;
      }

      this.configService
        .getDropdownOptions(
          field.optionsApiUrl,
          field.optionsPayload || {},
          field.optionsMethod || 'get',
          this.data?.headers,
        )
        .subscribe({
        next: (response: any) => {
          const options = this.normalizeOptions(response, field);
          if (Array.isArray(options)) {
            field.options = options;
          }
        },
        error: () => {
          field.options = field.options || [];
        },
      });
    });
  }

  private normalizeOptions(response: any, field: FormFieldConfig): FormFieldOption[] {
    const rawOptions =
      this.getByPath(response, field.optionsResponsePath) ||
      this.getByPath(response, `dropdowns.${field.key}`) ||
      this.getByPath(response, `options.${field.key}`) ||
      this.getByPath(response, `data.${field.key}`) ||
      this.getByPath(response, field.key) ||
      (Array.isArray(response) ? response : response?.data || response?.options || []);

    if (!Array.isArray(rawOptions)) {
      return [];
    }

    const labelKey = field.optionsLabelKey || 'label';
    const valueKey = field.optionsValueKey || 'value';

    return rawOptions.map((option) => {
      if (typeof option !== 'object' || option === null) {
        return option;
      }

      return {
        label: option[labelKey] ?? option.name ?? option.text ?? option[valueKey] ?? '',
        value: option[valueKey] ?? option.id ?? option[labelKey] ?? '',
      };
    });
  }

  private getByPath(source: any, path?: string): any {
    if (!source || !path) {
      return undefined;
    }

    return path.split('.').reduce((value, key) => value?.[key], source);
  }
}
